import { isNil } from 'lodash';
import qs from 'query-string';

import { LIST_PAGINATION_MODEL } from '../../store/modules/app/models';
import CategoryApiV2 from '../../lib/api/category/CategoryApiV2';

/**
 * 카테고리 상품 리스트 검색 시 사용되는 쿼리를 조작하는 custom hook
 * @return {{setQueryString: ((function(string, Object): (string|*))|*), queryFn: (function(number, string): {filterOption: {name: (Array<string|boolean|number|null>|string|boolean|number|null), orderBy: (Array<string|boolean|number|null>|string|boolean|number), storeName: (Array<string|boolean|number|null>|string|boolean|number|null), sortBy: (Array<string|boolean|number|null>|string|boolean|number), id: (Array<string|boolean|number|null>|string|boolean|number|null), storeId: (Array<string|boolean|number|null>|string|boolean|number|null), storeStatus: (Array<string|boolean|number|null>|string|boolean|number|null), status: (Array<string|boolean|number|null>|string|boolean|number|null)}, pagination: {pages: number, pageSize: number, pageNum: number}})}}
 */
const useCategoryProductListQueryOption = () => {
  /**
   * 쿼리스트링을 기존에 사용하던 filterOption, pagination 에 맞춰 파싱
   * @param {string} queryString - 카테고리 상품 리스트 검색 필터 옵션을 토대로 생성된 쿼리스트링
   * @param {number} totalCnt - 검색 결과 갯수
   * @return {{filterOption: {name: (Array<string | boolean | number | null>|string|boolean|number|null), orderBy: (Array<string | boolean | number | null>|string|boolean|number|string), storeName: (Array<string | boolean | number | null>|string|boolean|number|null), sortBy: (Array<string | boolean | number | null>|string|boolean|number|string), id: (Array<string | boolean | number | null>|string|boolean|number|null), storeId: (Array<string | boolean | number | null>|string|boolean|number|null), storeStatus: (Array<string | boolean | number | null>|string|boolean|number|null), status: (Array<string | boolean | number | null>|string|boolean|number|null)}, pagination: {pages: number, pageSize: (number|number), pageNum: (number|number)}}}
   */
  const queryStringParser = (queryString) => {
    const query = qs.parse(queryString);
    const pageSize = Number(query?.pageSize) || LIST_PAGINATION_MODEL.pageSize;
    const pageNum = Number(query?.page) - 1 || LIST_PAGINATION_MODEL.pageNum;

    return {
      filterOption: {
        // 브랜드 명
        brandName: query?.brandName || null,
        // 브랜드 ID
        brandId: isNil(query?.brandId) ? null : Number(query.brandId),
        // 브랜드 상태
        brandStatus: isNil(query?.brandStatus) ? null : query.brandStatus,
        // 상품 ID
        productId: Number(query?.productId) || null,
        // 상품 명
        productName: query?.productName || null,
        // 판매 상태
        productStatus: isNil(query?.productStatus) ? null : query.productStatus,
        //메인 카테고리 id
        categoryId: query?.categoryId || null,
        // 정렬 기준(default: productCategoryLinkDate - 카테고리와 상품이 연결된 날짜)
        sort: query?.sort?.split(',') || [],
      },
      pagination: {
        pageSize,
        pageNum,
      },
    };
  };

  /**
   * queryStringParser를 통해 쿼리스트링을 기존에 사용하던 filterOption, pagination 에 맞춰 파싱한 다음 fetch Data와 함께 반환하는 함수
   * @param {number} categoryId - 카테고리 id
   * @param {string} queryString - 카테고리 상품 리스트 검색 필터 옵션을 토대로 생성된 쿼리스트링
   * @return {Promise<{filterOption: {name: (Array<string|boolean|number|null>|string|boolean|number|null), orderBy: (Array<string|boolean|number|null>|string|boolean|number), storeName: (Array<string|boolean|number|null>|string|boolean|number|null), sortBy: (Array<string|boolean|number|null>|string|boolean|number), id: (Array<string|boolean|number|null>|string|boolean|number|null), storeId: (Array<string|boolean|number|null>|string|boolean|number|null), storeStatus: (Array<string|boolean|number|null>|string|boolean|number|null), status: (Array<string|boolean|number|null>|string|boolean|number|null)}, pagination: {pages: number, pageSize: number, pageNum: number}}>}
   */
  const queryFn = async (categoryId, queryString) => {
    const options = queryStringParser(queryString);
    const result = await CategoryApiV2.getProductList(categoryId, {
      ...options.filterOption,
      page: options.pagination.pageNum + 1,
      pageSize: options.pagination.pageSize,
    });

    return {
      ...result,
      ...options,
      pagination: {
        ...options.pagination,
        pages: result ? Math.ceil(result.totalCount / options.pagination.pageSize) : 0,
      },
    };
  };

  /**
   * queryString을 생성하여 반환하는 함수
   * queryString이 없을 경우 options의 initialData 에 맞춰 쿼리스트링을 생성하여 반환
   * @param {string} queryString - 카테고리 상품 리스트 검색 필터 옵션을 토대로 생성된 쿼리스트링
   * @param {object} options - react-query options 객체
   * @return {string|*}
   */
  const setQueryString = (queryString, options) => {
    if (queryString !== '?tabKey=1') return queryString;

    if (options?.initialData) {
      const query = {
        // 페이지 사이즈
        pageSize: options.initialData.pagination.pageSize,
        // 페이지 숫자
        page: options.initialData.pagination.pageNum + 1,
        // 정렬 기준(default: productCategoryLinkDate - 카테고리와 상품이 연결된 날짜)
        sort: options.initialData.filterOption.sort,
      };
      return '?tabKey=1&' + qs.stringify(query);
    }

    return '';
  };

  return { setQueryString, queryFn };
};

export default useCategoryProductListQueryOption;
